import type { TSwitchIntervalCardContent } from "@/components/shared/interval-switch/types.interval-switch";
import { PLAN_REF_ID_MAPPING_V1 } from "@/data/plans";
import { TAddOnRefId, TProductPricing, TProductRefId } from "@/types/plan";
import { CHECKOUT_COPY_CONFIG } from "./config.checkout";
import {
  CHECKOUT_TYPE,
  CHECKOUT_VARIANT,
  OFFER_UNAVAILABLE_REASON,
} from "./constants.checkout";

export type { PlanCardUIProps } from "./ui/PlanCardUI";

export type TAddOnRuleMap = Partial<Record<TProductRefId, TAddOnRefId[]>>;

export type TCheckoutCardPricing = TProductPricing;

export type TPlan = {
  id: string;
  planName: string;
  planDescription: string;
  promotionText: string;
  productRefId: TProductRefId;
  pricing: TCheckoutCardPricing;
  quantity?: number; // Optional quantity field for owned plans with quantities
};

export const USER_COHORT = {
  founders: "founders",
} as const;

export type TUserCohort = (typeof USER_COHORT)[keyof typeof USER_COHORT];

export type TAddOn = {
  id: string;
  name: string;
  description: string | null;
  productRefId: TAddOnRefId; // new
  featureList: string[]; // rich text

  pricing: TCheckoutCardPricing;

  user_cohorts: TUserCohort[] | null;
  max_count: number | null;
};

export type TAddOnRules = {
  productRefId: TProductRefId; // productRefId of the plan
  allowedAddOns: TAddOnRefId[]; // addOnRefId array that are allowed for this plan
};

export type TMigrationRules = Record<
  (typeof PLAN_REF_ID_MAPPING_V1)[keyof typeof PLAN_REF_ID_MAPPING_V1],
  {
    pre_selected: {
      product_id: TProductRefId | TAddOnRefId;
      quantity: number;
    }[];
    recommended: {
      product_id: TProductRefId | TAddOnRefId;
      quantity: number;
    }[];
  }
>;

type TUsageStatItem = {
  count: number;
  threshold: number;
  threshold_met: boolean;
};

export type TUsageStats = {
  culled_images: TUsageStatItem;
  edited_images: TUsageStatItem;
  retouched_images: TUsageStatItem;
  time_saved: number;
};

export type TCheckoutData = {
  planCards: TPlan[];
  addOnCards: TAddOn[]; // whatever we have
  addOnRules: TAddOnRules[];
  migrationRules: TMigrationRules;
  recommendationList: TProductRefId[]; // productRefId array
  highlightedProducts: TProductRefId[]; // productRefId array
  usageStats?: TUsageStats;
};

export type { TAddOnCartItem, TCartItem } from "../Cart/types.cart";

export interface CheckoutProduct {
  product_id: string;
  action: "add" | "remove";
  quantity?: number;
}

// config
export type TCheckoutType = (typeof CHECKOUT_TYPE)[keyof typeof CHECKOUT_TYPE];

export interface TBundledPlanConfig {
  id: string;
  name: string;
  description: string;
  badge: {
    text: string;
    type: "save" | "new" | "popular";
  };
  pricing: {
    originalPrice: number;
    discountedPrice: number;
    period: string;
  };
  button: {
    text: string;
    variant: "primary" | "secondary";
  };
}

// Type for individual checkout flow configs (plan_purchase, plan_manage, plan_migration)
export type TCheckoutFlowConfig =
  | (typeof CHECKOUT_COPY_CONFIG)["plan_purchase"]
  | (typeof CHECKOUT_COPY_CONFIG)["plan_manage"]
  | (typeof CHECKOUT_COPY_CONFIG)["plan_migration"];

export type TCheckoutSections = {
  statCard: { title: null };
  title: string;
  addOns: { title: string };
};

export type TCheckoutConfig = typeof CHECKOUT_COPY_CONFIG;

// Partial utility to make all properties from all union members available
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never;

// Merge all cart config types into one with optional properties
export type TCheckoutCartConfig = Partial<
  UnionToIntersection<
    | (typeof CHECKOUT_COPY_CONFIG)["plan_purchase"]["cart"]
    | (typeof CHECKOUT_COPY_CONFIG)["plan_manage"]["cart"]
    | (typeof CHECKOUT_COPY_CONFIG)["plan_migration"]["cart"]
  >
> & {
  // Required properties that exist in all configs
  buyingPlans: { title: string };
  checkoutButton: { default: string; resume: string };
  promoCode: { placeholder: string; applyText: string };
  // Optional properties that exist in some configs
  planChangeCard?: {
    title: string;
    subtitle: string;
    cta: {
      text: string;
    };
  };
};

export type TCheckoutVariant =
  (typeof CHECKOUT_VARIANT)[keyof typeof CHECKOUT_VARIANT];

export type TCheckoutDialog =
  | "none"
  | "pause"
  | "is-pause"
  | "cancel"
  | "cancel-complete"
  | "profile-manage"
  | "gallery-manage"
  | "plans-change"
  | "reactivation"
  | "legacy-plan";

export type TProfileRuleMap = Partial<Record<TProductRefId, "PROFILES"[]>>;

export type TAnnualSwitch =
  | {
      variant: "toggle";
      checked: boolean;
      disabled?: boolean;
      onToggle: (checked: boolean) => void;
      label?: string;
    }
  | {
      variant: "button";
      disabled?: boolean;
      onClick: () => void;
      label?: string;
    };

export type TCancelPlanRowConfig = {
  label: string;
};

/**
 * Copy for the cancel plan picker and the summary bar under it. Lives in
 * `data/content/en/cancel-plan-card.json`; typed here so the JSON stays
 * checkable and either component can be handed an override.
 */
export type TCancelPlanListConfig = {
  cancellingBadge: string;
  summary: {
    title: string;
    keepPrefix: string;
    /** Shown while nothing is ticked; `{0}` is the number of plans in the bundle */
    keepAllNote: string;
    /** Sits under `title` once every plan is ticked, in place of the keep note */
    cancelAllNote: string;
    currentLabel: string;
    afterLabel: string;
    /** Sits under the $0 after-price when the whole plan is going */
    endsNote: string;
    renewNote: string;
  };
  /** Per-product row labels; a product with no entry falls back to its plan name */
  rows: Partial<Record<TProductRefId, TCancelPlanRowConfig>>;
};

/** A {@link TCheckoutContent} entry rendered by `CancellationNotice` */
export type TCancellationNoticeConfig = {
  title: string;
  message?: string;
};

/**
 * Copy for the "choose which plans to cancel" dialog. Lives in
 * `data/content/en/cancel-complete-plan-card.json`.
 */
export type TCancelCompletePlanCardConfig = {
  title: string;
  description: string;
  /** Keyed off how much of the bundle is ticked (see `getCancelSummary`) */
  mainCTA: {
    /** Nothing ticked — the CTA is disabled, but still needs a label */
    idle: string;
    partial: string;
    /** Every plan ticked, so the subscription ends outright */
    cancelAll: string;
  };
  secondaryCTA: {
    /** Nothing ticked yet — the dialog is a no-op, so the CTA just closes it */
    idle: string;
    /** `{0}` is the bundle discount, e.g. `20%` */
    keepBundle: string;
    dismiss: string;
  };
  notices: {
    /**
     * Title takes `{0}` the plans ticked for cancellation, `{1}` the extra cost
     * of losing the bundle and `{2}` the billing suffix; message takes `{0}`
     * the discount.
     */
    bundleDiscount: Required<TCancellationNoticeConfig>;
    subscriptionEnd: Required<TCancellationNoticeConfig>;
  };
};

/** Copy for the cart summary. Lives in `data/content/en/checkout-cart.json`. */
export type TCheckoutCartContent = {
  forexAdjustmentLabel: string;
  ownedPlansTitle: string;
  currentPlanTitle: string;
  newPlanTitle: string;
  newAddOnTitle: string;
  endsOn: string;
  emptyState: string;
  checkoutButton: string;
  summaryLabel: string;
  renewsAt: string;
  renewsOn: string;
  invoiceNote: string;
  unusedDaysCredit: {
    label: string;
    value: string;
  };
};

export type TManagePlansSectionContent = {
  availableUpgrades: string;
};

export type TOfferUnavailableReason =
  (typeof OFFER_UNAVAILABLE_REASON)[keyof typeof OFFER_UNAVAILABLE_REASON];

/**
 * Every content JSON under `data/content/<locale>`, merged into one map.
 *
 * Components read this through `CheckoutService.Content` rather than importing
 * the JSON directly, so the locale is resolved in exactly one place.
 */
export type TCheckoutContent = {
  aiBundleRemovalNotice: TCancellationNoticeConfig;
  cancelPlanList: TCancelPlanListConfig;
  cancelCompletePlanCard: TCancelCompletePlanCardConfig;
  checkoutCart: TCheckoutCartContent;
  managePlansSection: TManagePlansSectionContent;
  switchIntervalCard: TSwitchIntervalCardContent;
  offerUnavailableToast: Record<TOfferUnavailableReason, string>;
};
